import React, { ReactNode } from "react";
import cn from "classnames";

import { EMPTY_VALUE } from "Shared/Constants/app";
import { DateKind, displayMoney, formatDate, formatMoney } from "Shared/Utils/display";
import { useDisplayFormats } from "Shared/Hook/useDisplayFormats";

import styles from "./cells.module.scss";
import SafeImage from "Components/UI/SafeImage/SafeImage";

export type Tone = "success" | "warning" | "danger" | "neutral";

export { EMPTY_VALUE };

export const StatusBadge = ({
  tone = "neutral",
  children,
}: {
  tone?: Tone;
  children: ReactNode;
}) => (
  <span className={cn(styles.badge, styles[`tone_${tone}`])}>
    <span className={styles.badgeDot} aria-hidden="true" />
    {children}
  </span>
);

export type StatusMap = Record<string, { label: string; tone: Tone }>;

/** "under_review" -> "Under review", for values a status map does not list. */
export const humanizeEnum = (value: string) => {
  const text = value.replace(/_/g, " ").trim();
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/** The StatusBadge for a value from a domain's status map. */
export const statusBadge = (map: StatusMap, value?: string | null) => {
  if (!value) return <EmptyValue />;
  const item = map[value];
  return (
    <StatusBadge tone={item?.tone}>{item?.label ?? humanizeEnum(value)}</StatusBadge>
  );
};

/** Primary line with an optional muted second line; the primary line truncates. */
export const TextCell = ({
  primary,
  secondary,
  icon,
  strong = false,
}: {
  primary: ReactNode;
  secondary?: ReactNode;
  icon?: ReactNode;
  strong?: boolean;
}) => (
  <div className={styles.text}>
    <span
      className={cn(styles.primary, strong && styles.primary_strong)}
      title={typeof primary === "string" ? primary : undefined}
    >
      {primary}
    </span>
    {secondary && (
      <span className={styles.secondary}>
        {icon}
        {secondary}
      </span>
    )}
  </div>
);

export const EntityCell = ({
  icon,
  image,
  title,
  subtitle,
}: {
  icon?: ReactNode;
  /** Photo URL; falls back to `icon` when missing. */
  image?: string | null;
  title: ReactNode;
  subtitle?: ReactNode;
}) => (
  <div className={styles.entity}>
    {(image || icon) && (
      <span className={styles.entityIcon}>
        <SafeImage
          className={styles.entityImage}
          src={image}
          alt=""
          width={34}
          height={34}
          fallback={icon}
        />
      </span>
    )}
    <TextCell primary={title} secondary={subtitle} strong />
  </div>
);

/** A table date in the company's date format (or time/dateTime); null when missing. */
export const formatTableDate = (value?: string | null, kind: DateKind = "date") =>
  formatDate(value, kind);

export const DateCell = ({
  value,
  secondary,
  emptyLabel = EMPTY_VALUE,
}: {
  value?: string | null;
  secondary?: ReactNode;
  emptyLabel?: ReactNode;
}) => {
  useDisplayFormats(); // re-render when the company's date format changes
  const formatted = formatTableDate(value);
  if (!formatted) return <span className={styles.empty}>{emptyLabel}</span>;
  return <TextCell primary={<span className={styles.numeric}>{formatted}</span>} secondary={secondary} />;
};

export const ProgressCell = ({
  value,
  tone,
}: {
  /** 0–100; null renders the empty value. */
  value?: number | null;
  tone?: Tone;
}) => {
  if (value === null || value === undefined || Number.isNaN(value))
    return <span className={styles.empty}>{EMPTY_VALUE}</span>;
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div className={styles.progress}>
      <span
        className={styles.progressTrack}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(clamped)}
      >
        <span
          className={cn(styles.progressBar, tone && styles[`bar_${tone}`])}
          style={{ width: `${clamped}%` }}
        />
      </span>
      <span className={cn(styles.numeric, clamped === 0 && styles.muted)}>
        {Number.isInteger(value) ? value : value.toFixed(1)}%
      </span>
    </div>
  );
};

/** Compact button for toolbars, bulk bars and cell actions. */
interface TableButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "ghost" | "outline" | "danger" | "primary";
  icon?: ReactNode;
}

export const TableButton: React.FC<TableButtonProps> = ({
  variant = "ghost",
  icon,
  children,
  className,
  ...props
}) => (
  <button
    type="button"
    {...props}
    className={cn(styles.button, styles[`button_${variant}`], className)}
  >
    {icon}
    {children}
  </button>
);

export { formatPersonName } from "Shared/Utils/personName";

/** Text link inside a clickable row; does not trigger the row click. */
export const CellLink = ({
  children,
  onClick,
  label,
}: {
  children: ReactNode;
  onClick: () => void;
  label?: string;
}) => (
  <button
    type="button"
    className={styles.link}
    aria-label={label}
    onClick={(event) => {
      event.stopPropagation();
      onClick();
    }}
  >
    {children}
  </button>
);

/** First item plus a "+N" count for the rest, e.g. labels or service tasks. */
export const ListCell = ({
  items,
  asTag = false,
}: {
  items: string[];
  asTag?: boolean;
}) => {
  const values = items.filter(Boolean);
  if (!values.length) return <EmptyValue />;
  const [first, ...rest] = values;
  return (
    <span className={styles.list} title={values.join(", ")}>
      <span className={cn(styles.listFirst, asTag && styles.tag)}>{first}</span>
      {rest.length > 0 && <span className={styles.more}>+{rest.length}</span>}
    </span>
  );
};

const moneyFormat = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const toFiniteNumber = (value?: number | string | null) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Money formatting lives in Shared/Utils/display (company currency).
export { formatMoney, displayMoney };

/** Two decimals with grouping, e.g. "1,204.50"; null when there is no number. */
export const formatDecimal = (value?: number | string | null) => {
  const parsed = toFiniteNumber(value);
  return parsed === null ? null : moneyFormat.format(parsed);
};

export const MoneyCell = ({ value }: { value?: number | string | null }) => {
  const formatted = formatMoney(value);
  return formatted ? (
    <span className={styles.numeric}>{formatted}</span>
  ) : (
    <EmptyValue />
  );
};

export const NumberCell = ({
  value,
  suffix,
}: {
  value?: number | string | null;
  suffix?: string;
}) => {
  if (value === null || value === undefined || value === "") return <EmptyValue />;
  const parsed = Number(value);
  return (
    <span className={styles.numeric}>
      {Number.isFinite(parsed) ? parsed.toLocaleString("en-US") : value}
      {suffix && ` ${suffix}`}
    </span>
  );
};

export const CheckCell = ({ value, label }: { value: boolean; label: string }) =>
  value ? (
    <svg className={styles.check} viewBox="0 0 24 24" role="img" aria-label={label}>
      <path d="m5 12 5 5 9-10" />
    </svg>
  ) : (
    <EmptyValue />
  );

/** Header text for screen readers only, e.g. an actions column. */
export const HiddenLabel = ({ children }: { children: ReactNode }) => (
  <span className={styles.srOnly}>{children}</span>
);

export const EmptyValue = () => <span className={styles.empty}>{EMPTY_VALUE}</span>;

export const TableIconFrame = ({ children }: { children: ReactNode }) => (
  <span className={styles.entityIcon}>{children}</span>
);
